import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Card, 
  Box,
  Container,
  Grid,
  IconButton,
  Alert
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Facebook,
  Twitter,
  LinkedIn
} from '@mui/icons-material';
import logo0 from '../assets/logo0.png';
import backgroundImage from '../assets/backgroundlogin.png';

// Reuse the same styled components
const LoginCard = styled(Card)({
  maxWidth: '500px',
  width: '100%',
  padding: '40px',
  backgroundColor: '#fff',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  borderRadius: '4px',
});

const StyledTextField = styled(TextField)({
  marginBottom: '24px',
  '& .MuiOutlinedInput-root': {
    height: '48px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#007C3F',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    transform: 'translate(14px, 14px) scale(1)',
  },
});

const SignUpButton = styled(Button)({
  backgroundColor: '#007C3F',
  color: '#fff',
  padding: '12px',
  fontSize: '16px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#006F3C',
  },
});

const HelpLink = styled(Link)({
  color: '#007C3F',
  textDecoration: 'none',
  fontSize: '14px',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const StyledFooter = styled(Box)({
  backgroundColor: '#333333',
  color: '#fff',
  padding: '48px 0 24px',
  marginTop: '64px',
});

const FooterLink = styled(Link)({
  color: '#fff',
  textDecoration: 'none',
  fontSize: '14px',
  display: 'block',
  marginBottom: '8px',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const FooterHeading = styled(Typography)({
  color: '#fff',
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '16px',
});

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Add your registration logic here
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        zIndex: 0
      },
      '& > *': {
        position: 'relative',
        zIndex: 1
      }
    }}>
      {/* Header */}
      <Box 
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#000000'
        }}
      >
        <Box 
          sx={{
            maxWidth: '800px',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Link to="/" style={{ textDecoration: 'none' }}>
            <img 
              src={logo0} 
              alt="Logo" 
              style={{ 
                height: '30px',
                filter: 'brightness(0) invert(1)'
              }}
            />
          </Link>
        </Box>
      </Box>

      <Box sx={{
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 4,
        pb: 4
      }}>
        <LoginCard>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">Account Created Successfully!</Typography>
              <Typography variant="body2">
                Please check your email to verify your account.
              </Typography>
              <Button
                onClick={() => navigate('/login')}
                sx={{ mt: 2, color: '#007C3F' }}
              >
                Go to Login
              </Button>
            </Alert>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 4, textAlign: 'center', fontWeight: 500 }}>
                Create Your Account
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <div>
                    <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                      First Name
                    </Typography>
                    <StyledTextField
                      fullWidth
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                      Last Name
                    </Typography>
                    <StyledTextField
                      fullWidth
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                  Email Address
                </Typography>
                <StyledTextField
                  fullWidth
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                  Password
                </Typography>
                <StyledTextField
                  fullWidth
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />

                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                  Confirm Password
                </Typography>
                <StyledTextField
                  fullWidth
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />

                <SignUpButton
                  type="submit"
                  fullWidth
                  disabled={loading}
                >
                  Create Account
                </SignUpButton>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Already have an account?{' '}
                    <HelpLink to="/login">
                      Sign in here
                    </HelpLink>
                  </Typography>
                </Box>
              </form>
            </>
          )}
        </LoginCard>
      </Box>

      {/* Footer */}
      <StyledFooter>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <FooterHeading>Products</FooterHeading>
              <FooterLink to="/domains">Domains</FooterLink>
              <FooterLink to="/hosting">Hosting</FooterLink>
              <FooterLink to="/email">Email</FooterLink>
              <FooterLink to="/ssl">SSL Certificates</FooterLink>
            </Grid>

            <Grid item xs={12} md={3}>
              <FooterHeading>Company</FooterHeading>
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
              <FooterLink to="/careers">Careers</FooterLink>
            </Grid>

            <Grid item xs={12} md={3}>
              <FooterHeading>Legal</FooterHeading>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/gdpr">GDPR</FooterLink>
            </Grid>

            <Grid item xs={12} md={3}>
              <FooterHeading>Support</FooterHeading>
              <FooterLink to="/help">Help Center</FooterLink>
              <FooterLink to="/status">System Status</FooterLink>
              <FooterLink to="/api">API Documentation</FooterLink>
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: 4, 
            pt: 2, 
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.7 }}>
                © {new Date().getFullYear()} Your Company
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton size="small" sx={{ color: '#fff' }}>
                <Facebook fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#fff' }}>
                <Twitter fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#fff' }}>
                <LinkedIn fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </StyledFooter>
    </Box>
  );
};

export default Signup; 